import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GlobalSearch from './GlobalSearch';
import './AppHeader.css';

/**
 * AppHeader — greytHR-style top header bar with breadcrumb, quick links, and notification bell.
 * Shown on all authenticated pages except the Home page.
 */
const AppHeader: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  // Ctrl+K / Cmd+K to open search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Build breadcrumb from path
  const buildBreadcrumb = (): { label: string; path?: string }[] => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path?: string }[] = [];

    const labelMap: Record<string, string> = {
      performance: 'Goal Plans',
      objectives: 'My Objectives',
      new: 'New Objective',
      team: 'Team Objectives',
      approvals: 'Pending Approvals',
      reviews: 'My Reviews',
      'team-reviews': 'Team Reviews',
      analytics: 'Analytics',
      'review-cycles': 'Review Cycles',
      reports: 'Reports',
      'lock-management': 'Lock Management',
      dashboard: 'Dashboard',
      expenses: 'Expenses',
      nominations: 'Nominations',
      admin: 'Admin',
      voting: 'Voting',
    };

    let pathSoFar = '';
    for (const seg of segments) {
      pathSoFar += `/${seg}`;
      const label = labelMap[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
      crumbs.push({ label, path: pathSoFar });
    }
    return crumbs;
  };

  const breadcrumbs = buildBreadcrumb();

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  return (
    <header className="app-header">
      <div className="app-header__left">
        <nav className="app-header__breadcrumb" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="app-header__breadcrumb-sep">/</span>}
              {i < breadcrumbs.length - 1 && crumb.path ? (
                <button
                  className="app-header__breadcrumb-link"
                  onClick={() => navigate(crumb.path!)}
                >
                  {crumb.label}
                </button>
              ) : (
                <span className="app-header__breadcrumb-current">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      <div className="app-header__right">
        {/* Search Button */}
        <button
          className="app-header__search-btn"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="app-header__search-label">Search</span>
          <kbd className="app-header__search-shortcut">⌘K</kbd>
        </button>

        {/* User Avatar */}
        {user && (
          <div className="app-header__user">
            <div className="app-header__avatar">
              {getInitials(user.name)}
            </div>
          </div>
        )}
      </div>

      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default AppHeader;
